import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ServerConfigService } from '@app/configuration/server-config.service';
import { NotificationService } from '@features/notification/notification.service';
import { AssetType } from '@imcc/transport';
import { isPresent } from '@imcc/utilities';
import { plainToClass } from 'class-transformer';
import { EMPTY, Observable, throwError } from 'rxjs';
import {
  distinctUntilChanged, map, retry, shareReplay,
} from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { StateService } from '../state/state.service';
import { Collection } from './collection';
import { CollectionAction } from './collection-action.interface';
import { CollectionActions } from './collection-actions.enum';
import { CollectionState } from './collection-state.interface';
import { ICollectionAssetRequest } from './i-collection-asset-request';
import { ICollectionRequest } from './i-collection-request';

@Injectable({ providedIn: 'root' })
export class CollectionService {
  private _stateSnapshot: CollectionState;

  private _currentCollection: Collection;

  private _currentCollection$: Observable<Collection>;

  private collectionsUrl: string;

  private collectionMaxSize: number;

  collections$ = this.state$.pipe(
    map((state) => state.listOfCollections),
    distinctUntilChanged(),
  );

  constructor(
    private stateService: StateService,
    private http: HttpClient,
    private configService: ServerConfigService,
    private authService: AuthService,
    private notificationService: NotificationService,
  ) {
    this.initSubscriptions();
  }

  get stateSnapshot(): CollectionState {
    return this._stateSnapshot;
  }

  get state$(): Observable<CollectionState> {
    return this.stateService.selectDistinct('collection');
  }

  get currentCollection(): Collection {
    return (
      this.stateSnapshot.listOfCollections.find(
        (collection) => collection.collectionId === this.stateSnapshot.defaultCollectionId,
      )
      || this._currentCollection
      || new Collection()
    );
  }

  get currentCollection$(): Observable<Collection> {
    if (this._currentCollection$) {
      return this.currentCollection$;
    }

    return this.state$.pipe(
      map(
        (state) => state.listOfCollections.find(
          (collection) => collection.collectionId === state.defaultCollectionId,
        ) || this.currentCollection,
      ),
    );
  }

  dispatch = (type: CollectionActions, payload: Record<string, unknown> = {}) => {
    const values: CollectionAction = { type, ...payload };
    this.stateService.dispatch(values);
  };

  initSubscriptions = () => {
    this.state$.subscribe((state) => {
      this._stateSnapshot = state;
    });

    const { config } = this.configService;
    this.collectionMaxSize = config.collectionMaxSize;
    this.collectionsUrl = `${config.apiBaseUrl}/collections`;
  };

  fetchCollections(): Observable<Collection[]> {
    return this.http
      .get(this.collectionsUrl)
      .pipe(map((result: Collection[]) => plainToClass(Collection, result)));
  }

  updateCollection = (collectionId: string | undefined = undefined) => {
    if (!this.authService.isAuthenticated()) {
      return undefined;
    }

    const newCollectionId = collectionId || this.stateSnapshot.defaultCollectionId;
    this.dispatch(CollectionActions.UPDATE_REQUEST);

    this.http.get(`${this.collectionsUrl}/${newCollectionId}`).subscribe(
      (result: Collection) => {
        const collection = plainToClass(Collection, result);
        this.dispatch(CollectionActions.UPDATE_SUCCESS, { collection });
      },
      (error) => {
        this.dispatch(CollectionActions.UPDATE_ERROR, { error });

        return EMPTY;
      },
    );

    return undefined;
  };

  isInCollection = (id: string, assetType: AssetType) => {
    if (!this.currentCollection) return false;

    return isPresent(
      this.currentCollection.assets.find(
        (asset) => id === asset.sourceAssetId && assetType === asset.assetType,
      ),
    );
  };

  deleteCollection = (collectionId: string | undefined = undefined) => {
    if (!this.authService.isAuthenticated()) {
      return undefined;
    }

    this.dispatch(CollectionActions.DELETE_REQUEST);

    const newCollectionId = collectionId || this.stateSnapshot.defaultCollectionId;
    if (!newCollectionId) {
      this.dispatch(CollectionActions.DELETE_ERROR, {
        error: 'No collectionId available',
      });

      return undefined;
    }

    return this.http
      .delete<void>(`${this.collectionsUrl}/${newCollectionId || ''}`)
      .pipe(retry(3))
      .subscribe(
        () => {
          this.dispatch(CollectionActions.DELETE_SUCCESS, {
            collectionId: newCollectionId,
          });
        },
        (error) => this.handleError(error, CollectionActions.DELETE_ERROR),
      );
  };

  setDefaultCollectionId(collectionId?: string) {
    this.dispatch(CollectionActions.SET_AS_DEFAULT, {
      collectionId,
    });
  }

  createCollection = (
    collection: ICollectionRequest,
    setAsDefaultCollection = true,
  ): Observable<Collection> => {
    this.dispatch(CollectionActions.CREATE_REQUEST);

    const request = this.http
      .post<Collection>(this.collectionsUrl, {
      name: collection.name,
      assets: collection.assets.map((x) => ({
        sourceAssetId: x.sourceAssetId,
        assetType: x.assetType,
      })),
    })
      .pipe(shareReplay(1));

    request.subscribe(
      (val) => {
        const createdCollection = plainToClass(Collection, val);
        this.dispatch(CollectionActions.CREATE_SUCCESS, {
          collection: createdCollection,
        });
        if (setAsDefaultCollection) {
          this.dispatch(CollectionActions.SET_AS_DEFAULT, {
            collectionId: createdCollection.collectionId,
          });
        }
      },
      (error) => this.handleError(error, CollectionActions.CREATE_ERROR),
    );

    return request;
  };

  addAssetToCollection = (
    asset: ICollectionAssetRequest | ICollectionAssetRequest[],
    collectionId: string | undefined = this.currentCollection.collectionId,
    setAsDefaultCollection = false,
  ) => {
    if (this.currentCollection.assets.length >= this.collectionMaxSize) {
      const { name } = this.currentCollection;

      this.notificationService.addNotification({
        type: 'warning',
        title: `Could not add asset to ${name}`,
        message: `Max collection size of ${this.collectionMaxSize} reached.`,
        timeOut: 10000,
      });

      return false;
    }

    const assetsToAdd = !Array.isArray(asset) ? [asset] : asset;

    if (!assetsToAdd.length) {
      return undefined;
    }

    if (!collectionId) {
      return this.createCollection(
        {
          name: 'My collection',
          assets: assetsToAdd,
        },
        true,
      );
    }

    this.dispatch(CollectionActions.ADD_ASSETS_REQUEST, {
      collectionId,
      assets: assetsToAdd,
    });

    this.http
      .patch<Collection>(`${this.collectionsUrl}/${collectionId}/add-assets`, {
      assets: assetsToAdd,
    })
      .subscribe(
        (result: Collection) => {
          const collection = plainToClass(Collection, result);
          this.dispatch(CollectionActions.ADD_ASSETS_SUCCESS, {
            collection,
          });

          this.notificationService.addNotification({
            type: 'success',
            title: 'Asset added',
            message: `${assetsToAdd[0].name} added to ${collection.name}.`,
            timeOut: 3000,
          });

          if (setAsDefaultCollection) {
            this.dispatch(CollectionActions.SET_AS_DEFAULT, {
              collectionId: collection.collectionId,
            });
          }
        },
        (error) => this.handleError(error, CollectionActions.ADD_ASSETS_ERROR),
      );

    return undefined;
  };

  removeAssetFromCollection = (
    asset: ICollectionAssetRequest | ICollectionAssetRequest[],
    collectionId: string | undefined = this.stateSnapshot.defaultCollectionId,
  ) => {
    const assetsToRemove = !Array.isArray(asset) ? [asset] : asset;

    if (!assetsToRemove.length) return;

    this.dispatch(CollectionActions.REMOVE_ASSETS_REQUEST, {
      collectionId,
      assets: assetsToRemove,
    });

    this.http
      .patch<Collection>(`${this.collectionsUrl}/${collectionId}/remove-assets`, {
      assets: assetsToRemove,
    })
      .subscribe(
        (result: Collection) => {
          const collection = plainToClass(Collection, result);

          this.notificationService.addNotification({
            type: 'success',
            title: 'Asset removed',
            message: `${assetsToRemove[0].name} removed from ${collection.name}.`,
            timeOut: 3000,
          });

          this.dispatch(CollectionActions.REMOVE_ASSETS_SUCCESS, {
            collection,
          });
        },
        (error) => this.handleError(error, CollectionActions.REMOVE_ASSETS_ERROR),
      );
  };

  updateName = (
    name: string,
    collectionId: string = this.stateSnapshot.defaultCollectionId as string,
  ) => {
    const collection = this.stateSnapshot.listOfCollections.find(
      (b) => b.collectionId === collectionId,
    );
    this.dispatch(CollectionActions.CHANGE_NAME_REQUEST, {
      collection: { ...collection, name },
    });
    this.http
      .post<Collection>(`${this.collectionsUrl}/${collectionId}/change-name`, {
      name,
    })
      .subscribe(
        (result: Collection) => {
          const updatedCollection = plainToClass(Collection, result);
          this.dispatch(CollectionActions.CHANGE_NAME_SUCCESS, {
            collection: updatedCollection,
          });
        },
        (error) => this.handleError(error, CollectionActions.CHANGE_NAME_ERROR),
      );
  };

  private handleError = (error: HttpErrorResponse, action: CollectionActions) => {
    const errorMessage = this.getErrorMessage(error);
    this.dispatch(action, { payload: { errorMessage } });

    return throwError('Something bad happened; please try again later.');
  };

  private getErrorMessage = (error: HttpErrorResponse) => {
    if (error.error instanceof ErrorEvent) {
      return error.error.message;
    }

    return error.error;
  };
}
